import { privateKeyToAccount } from 'viem/accounts'
import { z } from 'zod'

/**
 * Aster v3 の署名で利用する EIP-712 chainId。
 */
export const asterV3ChainId = 1666

/**
 * Aster v3 の署名対象メッセージ種別。
 */
export const asterV3MessageTypes = {
  Message: [{ name: 'msg', type: 'string' }],
} as const

/**
 * Aster v3 の EIP-712 ドメイン定義。
 */
export const asterV3Domain = {
  name: 'AsterSignTransaction',
  version: '1',
  chainId: asterV3ChainId,
  verifyingContract: '0x0000000000000000000000000000000000000000',
} as const

/**
 * Aster v3 の署名関数シグネチャ。
 * EIP-712 ドメイン・型は Aster v3 固定のため、呼び出し側は
 * `msg` (= クエリ文字列) を渡すだけで良い。
 */
export type SignAsterV3Payload = (msg: string) => Promise<`0x${string}`>

const evmAddressSchema = z.custom<`0x${string}`>(
  (value) => typeof value === 'string' && /^0x[a-fA-F0-9]{40}$/.test(value),
)

let lastNonceMilliseconds = 0
let nonceSequence = 0

/**
 * Aster v3 用の認証情報（address 2 つ）を検証する。
 * 秘密鍵はクライアント側に保存しない。
 * @param params - 検証対象
 * @param params.signer - signer address
 * @param params.user - user address
 * @returns 正規化済み address
 */
export const normalizeAsterV3Auth = (params: {
  signer: string
  user: string
}): {
  signer: `0x${string}`
  user: `0x${string}`
} => {
  const { signer, user } = params
  return {
    user: evmAddressSchema.parse(user),
    signer: evmAddressSchema.parse(signer),
  }
}

/**
 * Aster v3 の nonce をマイクロ秒精度で生成する。
 * @returns nonce 文字列
 */
export const createAsterV3Nonce = (): string => {
  const nowMilliseconds = Date.now()

  if (nowMilliseconds === lastNonceMilliseconds) {
    nonceSequence += 1
  } else {
    lastNonceMilliseconds = nowMilliseconds
    nonceSequence = 0
  }

  return String(nowMilliseconds * 1000 + nonceSequence)
}

/**
 * Aster v3 用の署名対象クエリ文字列を生成する。
 * 公式 Python SDK の `get_url()` 実装に合わせ、パラメータは挿入順で
 * URL エンコードせずに `key=value&key=value` 形式で連結する。
 * @param values - パラメータ
 * @returns 連結済みクエリ文字列 (エンコードなし)
 */
export const buildAsterV3QueryString = (
  values: Record<string, string>,
): string => {
  return Object.entries(values)
    .map(([key, value]) => `${key}=${value}`)
    .join('&')
}

/**
 * Aster v3 リクエストに必須の `asterChain` パラメータ値。
 * chainId 1666 (Mainnet) / 714 (Testnet) と対応する。
 */
export const asterV3ChainParam = 'Mainnet'

/**
 * Signer の秘密鍵から Aster v3 用の EIP-712 署名関数を生成する。
 * 秘密鍵はローカル DB に保存された Cryptorch 自動生成の signer key を想定。
 * ウォレット拡張を介さず純粋にローカルで署名するため、ユーザー操作なしで
 * 大量のリクエストを連続署名できる。
 * @param privateKey - signer の秘密鍵 (hex)
 * @returns Aster v3 の `msg` を受け取り EIP-712 署名を返す関数
 */
export const createAsterV3LocalSigner = (
  privateKey: string,
): SignAsterV3Payload => {
  const account = privateKeyToAccount(privateKey as `0x${string}`)
  return (msg) =>
    account.signTypedData({
      domain: asterV3Domain,
      types: asterV3MessageTypes,
      primaryType: 'Message',
      message: { msg },
    })
}
